import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import bonafideService from "../../services/bonafideService";
import studentService from "../../services/studentService";

const todayDMY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const EMPTY_FORM = {
  student: "", srNo: "", certificateDate: todayDMY(),
  className: "", sessionStart: String(new Date().getFullYear()),
  sessionEnd: String(new Date().getFullYear() + 1),
};

const asTrimmedString = (value) => String(value ?? "").trim();

export default function BonafideForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { academicYear } = useApp();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await bonafideService.getById(id);
        const c = res.data;
        setForm({ student: c.student._id, srNo: String(c.srNo), certificateDate: c.certificateDate, className: c.className, sessionStart: c.sessionStart, sessionEnd: c.sessionEnd });
        setSelectedStudent(c.student);
        setStudentSearch(c.student.fullName);
      } catch (err) { toast.error(err.message); navigate("/bonafide"); }
      finally { setFetching(false); }
    })();
  }, [id, isEdit, navigate]);

  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(async () => {
      if (!asTrimmedString(studentSearch)) { setStudents([]); return; }
      setLoadingStudents(true);
      try {
        const res = await studentService.getAll({ search: studentSearch, limit: 8, academicYear: academicYear?._id || "" });
        setStudents(res.data);
      } catch { setStudents([]); }
      finally { setLoadingStudents(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [studentSearch, isEdit, academicYear]);

  const selectStudent = (s) => {
    setSelectedStudent(s); setStudents([]);
    setForm((p) => ({ ...p, student: s._id, className: s.currentClass }));
    setStudentSearch(s.name);
    if (errors.student) setErrors((p) => ({ ...p, student: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.student) errs.student = "Please select a student";
    if (!asTrimmedString(form.srNo)) errs.srNo = "Sr. No. is required";
    if (isNaN(Number(form.srNo))) errs.srNo = "Must be a number";
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(form.certificateDate)) errs.certificateDate = "Format: DD/MM/YYYY";
    if (!asTrimmedString(form.className)) errs.className = "Required";
    if (!/^\d{4}$/.test(form.sessionStart)) errs.sessionStart = "4-digit year";
    if (!/^\d{4}$/.test(form.sessionEnd)) errs.sessionEnd = "4-digit year";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (!isEdit && !academicYear?._id) { toast.error("Select an Academic Year from the Dashboard first."); return; }

    setLoading(true);
    try {
      const payload = { ...form, srNo: Number(form.srNo), ...(!isEdit && { academicYear: academicYear._id }) };
      if (isEdit) { await bonafideService.update(id, payload); toast.success("Certificate updated"); }
      else { await bonafideService.create(payload); toast.success("Certificate issued"); }
      navigate("/bonafide");
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  if (fetching) return <div className="loading-center"><div className="spinner" />Loading...</div>;

  return (
    <div style={{ maxWidth: 700 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{isEdit ? "Edit Bonafide Certificate" : "Issue Bonafide Certificate"}</h1>
          <p className="page-subtitle">{academicYear ? `Academic Year: ${academicYear.label}` : ""}</p>
        </div>
        <button className="btn btn-outline" onClick={() => navigate("/bonafide")}>← Back</button>
      </div>

      {!isEdit && !academicYear && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#dc2626" }}>
          ⚠️ No academic year selected. Go to Dashboard first.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Student */}
        <div className="card" style={{ marginBottom: "1rem" }}>
          <div className="card-header">Student</div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Select Student <span style={{ color: "var(--danger)" }}>*</span></label>
              {!isEdit && (
                <div className="student-select-box">
                  <div className="student-select-search">
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "#9ca3af" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input placeholder="Type student name or enrollment no..." value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); if (selectedStudent) { setSelectedStudent(null); setForm((p) => ({ ...p, student: "" })); } }} />
                    {loadingStudents && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>…</span>}
                  </div>
                  {students.length > 0 && (
                    <div className="student-select-list">
                      {students.map((s) => (
                        <div key={s._id} className={`student-select-item ${selectedStudent?._id === s._id ? "selected" : ""}`} onClick={() => selectStudent(s)}>
                          <div className="student-name">{s.fullName}</div>
                          <div className="student-meta">{s.enrollmentNo} · {s.currentClass} · DOB: {s.dob}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {selectedStudent && (
                <div style={{ marginTop: "0.5rem", padding: "0.65rem 0.85rem", background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: "var(--radius)", fontSize: 13 }}>
                  <strong>{selectedStudent.fullName}</strong>
                  <span style={{ color: "var(--text-muted)", marginLeft: "0.75rem" }}>{selectedStudent.enrollmentNo} · DOB: {selectedStudent.dob}</span>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>DOB in words: <em>{selectedStudent.dobInWords}</em></div>
                </div>
              )}
              {errors.student && <span className="form-error">{errors.student}</span>}
            </div>
          </div>
        </div>

        {/* Certificate Details */}
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <div className="card-header">Certificate Details</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Sr. No. <span style={{ color: "var(--danger)" }}>*</span></label>
                <input type="number" name="srNo" value={form.srNo} onChange={handleChange} min={1}
                  placeholder="From pre-printed form" className={`form-control ${errors.srNo ? "error" : ""}`} />
                <span className="form-hint">Read from the pre-printed certificate form</span>
                {errors.srNo && <span className="form-error">{errors.srNo}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Certificate Date <span style={{ color: "var(--danger)" }}>*</span></label>
                <input name="certificateDate" value={form.certificateDate} onChange={handleChange} placeholder="DD/MM/YYYY"
                  className={`form-control ${errors.certificateDate ? "error" : ""}`} />
                {errors.certificateDate && <span className="form-error">{errors.certificateDate}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Class / Course <span style={{ color: "var(--danger)" }}>*</span></label>
                <input name="className" value={form.className} onChange={handleChange} placeholder="e.g. D. Pharm 1st Year"
                  className={`form-control ${errors.className ? "error" : ""}`} />
                {errors.className && <span className="form-error">{errors.className}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Session Start <span style={{ color: "var(--danger)" }}>*</span></label>
                <input name="sessionStart" value={form.sessionStart} onChange={handleChange} maxLength={4} placeholder="2025"
                  className={`form-control ${errors.sessionStart ? "error" : ""}`} />
                {errors.sessionStart && <span className="form-error">{errors.sessionStart}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Session End <span style={{ color: "var(--danger)" }}>*</span></label>
                <input name="sessionEnd" value={form.sessionEnd} onChange={handleChange} maxLength={4} placeholder="2026"
                  className={`form-control ${errors.sessionEnd ? "error" : ""}`} />
                {errors.sessionEnd && <span className="form-error">{errors.sessionEnd}</span>}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate("/bonafide")}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Certificate" : "Issue Certificate"}
          </button>
        </div>
      </form>
    </div>
  );
}
